// src/modules/app-views/_apps/appmulti/coupons/pages/Document/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useMemo } from 'react';
import { Tabs } from 'antd';
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { buildInitialValues } from 'utils/mock';
import useFormInstance from 'hooks/useFormInstance';
import { buildOriginIcon } from 'components/JarvislyComponents/DocumentInfoIcons';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import RecordTab from './tabs/RecordTab';
import UsesTab from './tabs/UsesTab';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const isMockStep = false;
const STYLES = {
  cardHeaderWrapper: { position: 'relative' },
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CouponsDocument = props => {
  // ---------------------------------------------------------------------------
  // PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode } =
    useDocumentContext();

  // ---------------------------------------------------------------------------
  // HOOKS & REFS
  // ---------------------------------------------------------------------------
  const { upsert, urlInfo, get } = useZustand('global', 'useApi');
  const { formRef, isInitializedRef } = useFormInstance({ data });

  // ---------------------------------------------------------------------------
  // LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const _id = urlInfo?.params?._id;

  // ---------------------------------------------------------------------------
  // LOCAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // EFFECTS
  // ---------------------------------------------------------------------------
  const tabsItems = useMemo(() => {
    const src = Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
    return src;
  }, [availableTabs]);

  const initialValues = useMemo(() => buildInitialValues(isMockStep, data), [data]);

  useEffect(() => {
    const childProps = { ...props, data, initTab, viewMode };
    const itemsLocal = buildTabsArray(childProps);
    buildTabs(itemsLocal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initTab, viewMode, data?._id]);

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------
  const handleFinish = async ({ values, allValues }) => {
    const couponId = values?._id || allValues?._id || data?._id;
    const isNewDocument = !_id || _id === 'add';

    const dataToSave = { ...values };
    await upsert({ dataSet: dataToSave, _id: couponId });

    if (!isNewDocument) {
      await get();
    }
  };

  // ---------------------------------------------------------------------------
  // LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          initFocus="name"
          reloadEntirePage={true}
          ignoreResetFormOnSubmit={true}
          requiredFields={['name', 'discount', 'qty', 'expirationDate']}
          locked={viewMode === 'view'}
          showInfoMessage={false}
          initialValues={initialValues}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div style={STYLES.cardHeaderWrapper}>
            <CardHeaderWithCountry
              overlapsBackground
              data={data}
              avatarName={data?.name}
              watchField="countryCode"
            />
            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CouponsDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab, data } = props;
  return [
    {
      key: initTab,
      icon: buildOriginIcon(data),
      label: <span>{translateX('record')}</span>,
      hide: false,
      disabled: false,
      children: <RecordTab />,
    },
    {
      key: 'uses',
      label: <span>{translateX('usage_history')}</span>,
      hide: false,
      disabled: !data?._id,
      children: <UsesTab />,
    },
  ];
}
